// v2.0 — Code splitting via React.lazy — pages load only when first visited
//         Cuts initial bundle by ~60% — Login/Register load instantly,
//         Admin (1934 lines), AuditTrail, Reports load on demand only

import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { isSafeReturnPath } from './lib/safePath';
import { useAuth0 } from '@auth0/auth0-react';
import AppShell from './components/layout/AppShell';
import LoadingSpinner from './components/ui/LoadingSpinner';
import DeviceVerificationModal from './components/DeviceVerificationModal';
import { useCurrentUser } from './hooks';
import { useAuthStore } from './store';

// ── Eagerly loaded — needed immediately on any visit ──────────────────────────
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import RecordsDeliveryDownloadPage from './pages/records/RecordsDeliveryDownloadPage';
import VendorSignOffPage from './pages/records/VendorSignOffPage';

// ── Lazy loaded — downloaded only when user navigates to that page ────────────
const DashboardPage         = lazy(() => import('./pages/dashboard/DashboardPage'));
const TasksPage             = lazy(() => import('./pages/tasks/TasksPage'));
// Phase-2 v3 queue board — three-column My Work / Team Queue / Supervisor Exceptions
// surface (falls back to the tabbed TasksPage below lg).
const QueueBoardPage        = lazy(() => import('./pages/tasks/QueueBoardPage'));
const PatientsPage          = lazy(() => import('./pages/patients/PatientsPage'));
const AppointmentsPage      = lazy(() => import('./pages/appointments/AppointmentsPage'));
const ImportsPage           = lazy(() => import('./pages/imports/ImportsPage'));
// Bulk eligibility-PDF upload — platform-only internal tool (stage → verify → commit)
const EligibilityUploadsPage = lazy(() => import('./pages/platform/EligibilityUploadsPage'));
const ReportsPage           = lazy(() => import('./pages/reports/ReportsPage'));
const RecordsOpsPage        = lazy(() => import('./pages/reports/RecordsOpsPage'));
const AdminPage             = lazy(() => import('./pages/admin/AdminPage'));
const BillingPage           = lazy(() => import('./pages/billing/BillingPage'));
const AuditTrailPage        = lazy(() => import('./pages/audit/AuditTrailPage'));
// Phase 2.B — new full-screen onboarding wizard (replaces legacy AppShell modal)
const OnboardingWizardPage  = lazy(() => import('./pages/onboarding/OnboardingWizardPage'));
const DoctorReviewPage      = lazy(() => import('./pages/records/DoctorReviewPage'));
// Phase 2.E — activation + enterprise + pricing-admin pages
const ActivationPage             = lazy(() => import('./pages/activation/ActivationPage'));
const BaaSigningPage             = lazy(() => import('./pages/activation/BaaSigningPage'));
const EnterpriseRequestPage      = lazy(() => import('./pages/activation/EnterpriseRequestPage'));
const EnterpriseRequestsAdminPage = lazy(() => import('./pages/admin/EnterpriseRequestsPage'));
const PricingSettingsPage        = lazy(() => import('./pages/admin/PricingSettingsPage'));
// Phase 2.F — Sibia post-hoc backfill (legacy 'active' practices without BAA)
const BackfillPage               = lazy(() => import('./pages/backfill/BackfillPage'));
// Phase 2.G — Compliance Center for B+D combo (review + customize defaults)
const CompliancePage             = lazy(() => import('./pages/admin/CompliancePage'));
// Phase 2.G+ — My HIPAA Binder page (download every generated PDF)
const BinderPage                 = lazy(() => import('./pages/admin/BinderPage'));
const FaxConfigPage              = lazy(() => import('./pages/admin/FaxConfigPage'));
// Document Templates module Configure page (mig 055) — template library + packets
const DocumentTemplatesConfigPage = lazy(() => import('./pages/admin/DocumentTemplatesConfigPage'));
// Phase 3.B — Policy Update Wizard (re-acknowledge drifted binder docs)
const PolicyUpdateWizardPage     = lazy(() => import('./pages/policy-update/PolicyUpdateWizardPage'));
// Phase 5 — HIPAA training: staff hub (in AppShell) + full-screen taker (outside)
const MyTrainingPage             = lazy(() => import('./pages/training/MyTrainingPage'));
const TrainingTakerPage          = lazy(() => import('./pages/training/TrainingTakerPage'));
// Read-only review of a module already passed — writes nothing (see the file).
const ModuleReviewPage           = lazy(() => import('./pages/training/ModuleReviewPage'));
const ExamTakerPage              = lazy(() => import('./pages/training/ExamTakerPage'));
// Central Fax — inbox / queue / repository / triage / admin
const FaxInboxPage               = lazy(() => import('./pages/fax/FaxInboxPage'));
// Central Fax — standalone Send Fax module surface (compose + Sent rail)
const SendFaxPage                = lazy(() => import('./pages/fax/SendFaxPage'));
// AI Assist — ask one question about ONE patient's documents. Reached only by the
// 'ai_assist.use' capability grant; AppShell gates the nav entry, gate.ts gates the call.
const AiAssistAskPage            = lazy(() => import('./pages/ai-assist/AiAssistAskPage'));
// fax.7 Fax Insights is HELD (DO NOT DEPLOY) — de-wired for the F10 deploy 2026-06-16;
// re-target the dashboards to the TASK surface, then re-enable. Page file kept on disk.
// const FaxInsightsPage            = lazy(() => import('./pages/fax/FaxInsightsPage'));

function AppInitializer({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user: auth0User, logout } = useAuth0();
  const { isLoading: userLoading, error: userError } = useCurrentUser();
  const token = useAuthStore(s => s.token);

  // Phase A2 #9 — dedicated dead-end screen for an authenticated SSO user who
  // has NO RCMTask account (and whose domain didn't auto-join). The backend
  // returns 401 USER_NOT_FOUND; the interceptor preserves {message,code} and
  // skips the auto-logout for /auth/me, so the code reaches here. Without this,
  // the user lands on a blank/looping login with no explanation.
  const errCode = (userError as any)?.code as string | undefined;
  const isUnprovisioned =
    isAuthenticated && (errCode === 'USER_NOT_FOUND'
      || (userError as any)?.message?.includes?.('No account found'));
  if (isUnprovisioned) {
    const email = (auth0User?.email as string | undefined) ?? 'your account';
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F4F1EE]">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-4 text-2xl">⚠</div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Account not set up</h1>
          <p className="text-sm text-gray-600 mb-6">
            You're signed in as <span className="font-medium text-gray-800">{email}</span>, but this
            account isn't a member of any practice on RCMTask yet. Ask your practice administrator to
            invite you, then sign in again.
          </p>
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin + '/login' } })}
            className="px-4 py-2 bg-[#C94B1F] text-white text-sm font-semibold rounded-lg hover:bg-[#A8390F]"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Gate the child tree on the Bearer token actually landing in Zustand,
  // not just on the useCurrentUser query finishing. Many downstream queries
  // are `enabled: isAuthenticated` and would otherwise fire in parallel
  // before the axios request interceptor has a Bearer to attach — causing
  // a 401 flood across every endpoint.
  //
  // If useCurrentUser errors out (e.g., getAccessTokenSilently rejected),
  // fall through so the user can see whatever state is available and the
  // ProtectedRoute / login flow takes over. Don't hang on a permanent spinner.
  if (isAuthenticated && !userError && (userLoading || !token)) {
    return <LoadingSpinner />;
  }
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) {
    // Deep-link-after-login: preserve where the user was headed (e.g. the /doctor-review/:taskId
    // link in an approver notification email) across the SSO login wall. LoginPage threads this
    // into Auth0 appState.returnTo, which onRedirectCallback navigates to after the round-trip.
    try {
      const target = window.location.pathname + window.location.search;
      // isSafeReturnPath rejects protocol-relative ('//', '/\\') open-redirect paths.
      if (isSafeReturnPath(target) && target !== '/' && !target.startsWith('/login')) {
        sessionStorage.setItem('previsit_return_to', target);
      }
    } catch { /* ignore */ }
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Phase 2.B — redirects practice admins on /dashboard to /onboarding when their
// practice is in 'onboarding' status. Other paths (/onboarding itself, /admin,
// /billing, etc.) are accessible so they can manage account settings even
// before the wizard is complete. Platform users always bypass the redirect.
function OnboardingRedirect({ children }: { children: React.ReactNode }) {
  const { user, currentPractice } = useAuthStore();
  const path = window.location.pathname;
  const isPlatform = user?.user_type === 'platform_user';
  const needsOnboarding = currentPractice?.status === 'onboarding';
  const onWizard = path.startsWith('/onboarding');
  // Only force the redirect when a practice user with status='onboarding'
  // hits the dashboard (most common landing). They can still navigate
  // elsewhere manually if they need to.
  if (!isPlatform && needsOnboarding && path === '/dashboard') {
    return <Navigate to="/onboarding" replace />;
  }
  // If wizard is complete (status moved past 'onboarding') and user is on
  // /onboarding, send them home.
  if (!needsOnboarding && onWizard && currentPractice) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// ai.155 — the task email deep link. Every task email links to /tasks/<uuid>: notificationService
// sendEmail appends the stored notifications.link, which every sender of a task link writes in
// that bare path shape, to APP_URL. The app's own task deep-link contract is /tasks?task=<id>
// (TasksPage reads the task search key), and nothing routed /tasks/<uuid>, so the recipient saw
// the shell with an empty main area. This route sends that shape to the contract.
//  · replace, not push: the dead path must not stay in history — Back returns to where the user
//    came from instead of to a URL that bounces forward again.
//  · The stored link shape is deliberately NOT changed: communicationsService correlates the
//    records timeline on it. Redirecting here also repairs every email already delivered.
//  · NO UUID check here: TasksPage owns the one check and opens the drawer only for a UUID, so a
//    non-UUID lands on the task list, never on an empty shell.
//  · Any incoming query or hash is dropped: no sender adds one, so the target is one fixed shape.
//    URLSearchParams encodes the id, so a crafted segment cannot add a second search key.
// A component, not an inline Navigate: a Navigate target does not interpolate :id, so the param
// is read with useParams.
function TaskDeepLinkRedirect() {
  const { id } = useParams();
  const search = new URLSearchParams({ task: id ?? '' }).toString();
  return <Navigate to={`/tasks?${search}`} replace />;
}

export default function App() {
  const { isLoading } = useAuth0();
  if (isLoading) return <LoadingSpinner />;

  return (
    <BrowserRouter>
      <AppInitializer>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* MRR P3.4 — public secure single-use records-delivery link (no auth;
              the token IS the authorization; forwards to the API download route). */}
          <Route path="/records-delivery/:token" element={<RecordsDeliveryDownloadPage />} />
          {/* Onsite-retrieval vendor sign-off — public (no auth; the token is the authorization). */}
          <Route path="/vendor-sign-off/:token" element={<VendorSignOffPage />} />

          {/* Focused, mobile-friendly doctor review (Step 2 / Step 4 approval) — the
              records-release approver notification links here. Outside AppShell. */}
          <Route
            path="/doctor-review/:taskId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <DoctorReviewPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Phase 2.B — full-screen onboarding wizard (outside AppShell) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <OnboardingRedirect>
                    <OnboardingWizardPage />
                  </OnboardingRedirect>
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Phase 2.E — full-screen activation + BAA-signing pages (outside AppShell) */}
          <Route
            path="/activate"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <ActivationPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/activate/baa"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <BaaSigningPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/enterprise/upgrade"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <EnterpriseRequestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Stripe success URL for enterprise — also handled by webhook; this just
              shows a friendly success screen (re-uses BaaSigningPage's success state via redirect). */}
          <Route
            path="/enterprise/success"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <EnterpriseRequestPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Phase 2.F — full-screen post-hoc backfill (outside AppShell so it doesn't
              compete with the BackfillBanner shown inside AppShell). */}
          <Route
            path="/backfill"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <BackfillPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Phase 3.B — Policy Update Wizard (outside AppShell, like other wizards) */}
          <Route
            path="/policy-update"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <PolicyUpdateWizardPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Phase 5 — full-screen training taker (outside AppShell for focus) */}
          <Route
            path="/training/take/:assignmentId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <TrainingTakerPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Read-only Review — full-screen like the taker, but it is a READER:
              no attempt, no timer, no quiz, nothing written. Keyed on the MODULE
              (not an assignment) because reviewing outlives any one assignment. */}
          <Route
            path="/training/review/:moduleId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <ModuleReviewPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Exam Builder — full-screen exam taker (per-module wizard) */}
          <Route
            path="/exams/take/:examAssignmentId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamTakerPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <OnboardingRedirect>
                  <AppShell>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard"    element={<DashboardPage />} />
                        <Route path="/tasks"        element={<TasksPage />} />
                        <Route path="/tasks/:id"    element={<TaskDeepLinkRedirect />} />
                        <Route path="/queues"       element={<QueueBoardPage />} />
                        <Route path="/patients"     element={<PatientsPage />} />
                        <Route path="/appointments" element={<AppointmentsPage />} />
                        {/* Phase 5 — staff training hub */}
                        <Route path="/training"     element={<MyTrainingPage />} />
                        <Route path="/fax"          element={<FaxInboxPage />} />
                        <Route path="/send-fax"     element={<SendFaxPage />} />
                        {/* fax.7 /fax-insights — HELD (DO NOT DEPLOY); de-wired for the F10 deploy 2026-06-16. Re-enable after re-targeting the dashboards to the task surface. */}
                        {/* AI Assist — the ask screen. Inside the shell (it needs the practice
                            context the switcher owns). The route carries NO access decision of its
                            own: the nav entry is capability-gated in AppShell and the server's
                            services/aiAssist/gate.ts is what actually decides, so a user who types
                            the URL still gets a refusal from the door, not from the router. */}
                        <Route path="/ai-assist"    element={<AiAssistAskPage />} />
                        <Route path="/imports"      element={<ImportsPage />} />
                        {/* Platform-only bulk eligibility-PDF upload (stage → verify → commit) */}
                        <Route path="/eligibility-uploads" element={<EligibilityUploadsPage />} />
                        <Route path="/reports"      element={<ReportsPage />} />
                        <Route path="/records-ops"  element={<RecordsOpsPage />} />
                        <Route path="/admin"        element={<AdminPage />} />
                        <Route path="/billing"      element={<BillingPage />} />
                        <Route path="/audit"        element={<AuditTrailPage />} />
                        {/* Phase 2.E — admin pages (inside AppShell) */}
                        <Route path="/admin/enterprise-requests" element={<EnterpriseRequestsAdminPage />} />
                        <Route path="/admin/pricing"             element={<PricingSettingsPage />} />
                        {/* Phase 2.G — Compliance Center (review/customize PreVisit defaults) */}
                        <Route path="/admin/compliance"          element={<CompliancePage />} />
                        {/* Phase 2.G+ — My HIPAA Binder (download every generated PDF) */}
                        <Route path="/admin/binder"              element={<BinderPage />} />
                        <Route path="/admin/fax"                 element={<FaxConfigPage />} />
                        {/* Access core (D-2026-09-27-19 (7)) — Admin > Access, the shared Who can use panel. */}
                        <Route path="/admin/access"              element={<Navigate to="/admin?section=access" replace />} />
                        <Route path="/admin/document-templates"  element={<DocumentTemplatesConfigPage />} />
                      </Routes>
                    </Suspense>
                  </AppShell>
                </OnboardingRedirect>
              </ProtectedRoute>
            }
          />
        </Routes>
        {/* App-level device-trust gate (renders only when verification is required). */}
        <DeviceVerificationModal />
      </AppInitializer>
    </BrowserRouter>
  );
}
